const WEIGHT_LABEL_PATTERN = /重量|净重|毛重|单重|件重|商品重量|产品重量|参考重量|shipping weight|package weight|product weight|item weight|gross weight|net weight|weight/i

function formatWeight(kg: number): string {
  if (!Number.isFinite(kg) || kg <= 0) {
    return ''
  }

  return String(Number(kg.toFixed(3)))
}

function convertWeightToKg(value: number, unitHint = ''): number | null {
  if (!Number.isFinite(value) || value <= 0) {
    return null
  }

  const unit = String(unitHint || '').trim().toLowerCase()

  if (/kgs?|kilograms?|公斤|千克/i.test(unit)) {
    return value
  }

  if (/grams?|克|g\b/i.test(unit)) {
    return value / 1000
  }

  if (/斤/.test(unit)) {
    return value * 0.5
  }

  if (/lbs?|pounds?/i.test(unit)) {
    return value * 0.45359237
  }

  if (/oz|ounces?/i.test(unit)) {
    return value * 0.0283495231
  }

  return value > 50 ? value / 1000 : value
}

function parseWeightText(rawValue: unknown): string {
  const text = String(rawValue || '')
    .replace(/,/g, '')
    .trim()

  if (!text) {
    return ''
  }

  const matchers: Array<{ pattern: RegExp, unit: string }> = [
    { pattern: /(-?\d+(?:\.\d+)?)\s*(?:kgs?|kilograms?|公斤|千克)/i, unit: 'kg' },
    { pattern: /(-?\d+(?:\.\d+)?)\s*(?:grams?|克|g)(?:\b|\/|$)/i, unit: 'g' },
    { pattern: /(-?\d+(?:\.\d+)?)\s*斤(?:\b|\/|$)/, unit: '斤' },
    { pattern: /(-?\d+(?:\.\d+)?)\s*(?:lbs?|pounds?)(?:\b|\/|$)/i, unit: 'lb' },
    { pattern: /(-?\d+(?:\.\d+)?)\s*(?:oz|ounces?)(?:\b|\/|$)/i, unit: 'oz' },
  ]

  for (const { pattern, unit } of matchers) {
    const match = text.match(pattern)
    if (!match) {
      continue
    }

    const kg = convertWeightToKg(Number(match[1]), unit)
    const formatted = kg == null ? '' : formatWeight(kg)
    if (formatted) {
      return formatted
    }
  }

  const fallbackMatch = text.match(/-?\d+(?:\.\d+)?/)
  if (!fallbackMatch) {
    return ''
  }

  const kg = convertWeightToKg(Number(fallbackMatch[0]))
  return kg == null ? '' : formatWeight(kg)
}

export function normalizeEstimateWeight(value: unknown): string {
  if (value == null || value === '') {
    return ''
  }

  if (typeof value === 'number') {
    const kg = convertWeightToKg(value)
    return kg == null ? '' : formatWeight(kg)
  }

  if (typeof value === 'string') {
    return parseWeightText(value)
  }

  if (typeof value === 'object') {
    const rawObject = value as Record<string, unknown>
    const combined = rawObject.value != null
      ? `${String(rawObject.value)}${String(rawObject.unit || rawObject.weightUnit || '')}`
      : ''

    if (combined) {
      return parseWeightText(combined)
    }
  }

  return ''
}

export function resolveEstimateWeightFromCandidates(candidates: unknown[]): string {
  for (const candidate of candidates) {
    const normalized = normalizeEstimateWeight(candidate)
    if (normalized) {
      return normalized
    }
  }

  return ''
}

export function resolveEstimateWeightFromAttributeList(items: any[]): string {
  if (!Array.isArray(items) || !items.length) {
    return ''
  }

  for (const item of items) {
    const label = String(
      item?.attributeName
      || item?.propName
      || item?.name
      || item?.label
      || '',
    ).trim()

    if (!label || !WEIGHT_LABEL_PATTERN.test(label)) {
      continue
    }

    const normalized = resolveEstimateWeightFromCandidates([
      item?.value,
      item?.valueName,
      item?.valueDesc,
      item?.text,
      item?.attrValue,
      item?.userDef1,
      ...(Array.isArray(item?.values) ? item.values : []),
      ...(Array.isArray(item?.valuesTrans) ? item.valuesTrans : []),
    ])

    if (normalized) {
      return normalized
    }
  }

  return ''
}

export function buildMallEstimationQuery(params: {
  warehouseCode?: unknown
  countryCode?: unknown
  cargoTypeCode?: unknown
  weight?: unknown
  fallbackWeight?: unknown
  autoQuery?: boolean
}) {
  const query: Record<string, string> = {}
  const warehouseCode = String(params.warehouseCode || '').trim()
  const countryCode = String(params.countryCode || '').trim()
  const cargoTypeCode = String(params.cargoTypeCode || '').trim()
  const weight = normalizeEstimateWeight(params.weight)
    || normalizeEstimateWeight(params.fallbackWeight)

  if (warehouseCode) {
    query.warehouseCode = warehouseCode
  }

  if (countryCode) {
    query.countryCode = countryCode
  }

  if (cargoTypeCode) {
    query.cargoTypeCode = cargoTypeCode
  }

  if (weight) {
    query.weight = weight
  }

  if ((params.autoQuery ?? true) && query.countryCode && query.weight) {
    query.autoQuery = '1'
  }

  return query
}
